/**
 * プロジェクト全体の差分検出
 */

import type { MIDASProject } from '../../../types/project.types';
import type { PrimaryDataSet, DerivedDataSet } from '../../../types/dataset.types';
import type { DatasetId, ModelId, ReportId } from '../../../types/id.types';
import type {
  ProjectDiff,
  PrimaryDataSetDiff,
  ReportDiff,
  DiffOptions,
} from './types';
import { DEFAULT_DIFF_OPTIONS } from './types';
import { diffPrimaryDataset } from './primaryDatasetDiff';
import { diffReport } from './reportDiff';
import { hasChanges, getSimpleDiff } from './diffService';

/**
 * プロジェクト全体の差分を計算
 */
export function diffProject(
  original: MIDASProject,
  modified: MIDASProject,
  options: DiffOptions = {}
): ProjectDiff {
  const opts = { ...DEFAULT_DIFF_OPTIONS, ...options };

  const result: ProjectDiff = {
    hasDifferences: false,
    primaryDatasets: {
      added: [],
      removed: [],
      modified: {} as Record<DatasetId, PrimaryDataSetDiff>,
    },
    derivedDatasets: {
      added: [],
      removed: [],
      modified: [],
    },
    models: {
      hasDifferences: false,
      added: [],
      removed: [],
      modified: [],
    },
    reports: {
      hasDifferences: false,
      added: [],
      removed: [],
      modified: {} as Record<ReportId, ReportDiff>,
    },
    workspaces: {
      hasDifferences: false,
      added: [],
      removed: [],
      modified: [],
    },
    projectState: { hasDifferences: false },
    metadata: { hasDifferences: false },
  };

  // データセットの差分
  diffDatasets(original, modified, result, opts);

  // モデルの差分
  diffModels(original, modified, result);

  // レポートの差分
  diffReports(original, modified, result);

  // ワークスペースの差分
  diffWorkspaces(original, modified, result);

  // プロジェクト状態の差分
  result.projectState = getSimpleDiff(original.projectState, modified.projectState);

  // メタデータの差分
  result.metadata = getSimpleDiff(original.metadata, modified.metadata);

  // 全体の差分有無を判定
  result.hasDifferences =
    result.primaryDatasets.added.length > 0 ||
    result.primaryDatasets.removed.length > 0 ||
    Object.keys(result.primaryDatasets.modified).length > 0 ||
    result.derivedDatasets.added.length > 0 ||
    result.derivedDatasets.removed.length > 0 ||
    result.derivedDatasets.modified.length > 0 ||
    result.models.hasDifferences ||
    result.reports.added.length > 0 ||
    result.reports.removed.length > 0 ||
    Object.keys(result.reports.modified).length > 0 ||
    result.workspaces.hasDifferences ||
    result.projectState.hasDifferences ||
    result.metadata.hasDifferences;

  return result;
}

/**
 * データセットの差分を計算
 */
function diffDatasets(
  original: MIDASProject,
  modified: MIDASProject,
  result: ProjectDiff,
  options: Required<DiffOptions>
): void {
  const originalIds = Object.keys(original.datasets) as DatasetId[];
  const modifiedIds = Object.keys(modified.datasets) as DatasetId[];

  const originalSet = new Set(originalIds);
  const modifiedSet = new Set(modifiedIds);

  // 追加されたデータセット
  for (const id of modifiedIds) {
    if (!originalSet.has(id)) {
      const dataset = modified.datasets[id];
      if (dataset.type === 'primary') {
        result.primaryDatasets.added.push(id);
      } else if (dataset.type === 'derived') {
        result.derivedDatasets.added.push(id);
      }
      // ephemeralは無視（MDSに保存されない）
    }
  }

  // 削除されたデータセット
  for (const id of originalIds) {
    if (!modifiedSet.has(id)) {
      const dataset = original.datasets[id];
      if (dataset.type === 'primary') {
        result.primaryDatasets.removed.push(id);
      } else if (dataset.type === 'derived') {
        result.derivedDatasets.removed.push(id);
      }
    }
  }

  // 変更されたデータセット（両方に存在）
  for (const id of originalIds) {
    if (!modifiedSet.has(id)) continue;

    const originalDataset = original.datasets[id];
    const modifiedDataset = modified.datasets[id];

    // タイプが変わった場合は削除+追加として扱う
    if (originalDataset.type !== modifiedDataset.type) {
      if (originalDataset.type === 'primary') {
        result.primaryDatasets.removed.push(id);
      } else if (originalDataset.type === 'derived') {
        result.derivedDatasets.removed.push(id);
      }
      if (modifiedDataset.type === 'primary') {
        result.primaryDatasets.added.push(id);
      } else if (modifiedDataset.type === 'derived') {
        result.derivedDatasets.added.push(id);
      }
      continue;
    }

    // PrimaryDataSetの詳細比較
    if (originalDataset.type === 'primary' && modifiedDataset.type === 'primary') {
      const diff = diffPrimaryDataset(
        originalDataset as PrimaryDataSet,
        modifiedDataset as PrimaryDataSet,
        options
      );
      if (diff.hasDifferences) {
        result.primaryDatasets.modified[id] = diff;
      }
    }

    // DerivedDataSetの簡易比較（変更有無のみ）
    if (originalDataset.type === 'derived' && modifiedDataset.type === 'derived') {
      if (hasDerivedDatasetChanges(originalDataset, modifiedDataset)) {
        result.derivedDatasets.modified.push(id);
      }
    }
  }
}

/**
 * DerivedDataSetに変更があるかを判定
 * dataプロパティは比較対象外（キャッシュのため）
 */
function hasDerivedDatasetChanges(original: DerivedDataSet, modified: DerivedDataSet): boolean {
  // 名前の変更
  if (original.name !== modified.name) return true;

  // スキーマの変更
  if (hasChanges(original.schema, modified.schema)) return true;

  // 操作の変更
  if (hasChanges(original.operation, modified.operation)) return true;

  // 親データセットの変更
  if (hasChanges(original.parentDatasetIds, modified.parentDatasetIds)) return true;

  // メタデータの変更
  if (hasChanges(original.metadata, modified.metadata)) return true;

  // materialized設定の変更
  if (original.materialized !== modified.materialized) return true;

  return false;
}

/**
 * モデルの差分を計算
 */
function diffModels(
  original: MIDASProject,
  modified: MIDASProject,
  result: ProjectDiff
): void {
  const originalIds = Object.keys(original.models) as ModelId[];
  const modifiedIds = Object.keys(modified.models) as ModelId[];

  const originalSet = new Set(originalIds);
  const modifiedSet = new Set(modifiedIds);

  // 追加されたモデル
  for (const id of modifiedIds) {
    if (!originalSet.has(id)) {
      result.models.added.push(id);
      result.models.hasDifferences = true;
    }
  }

  // 削除されたモデル
  for (const id of originalIds) {
    if (!modifiedSet.has(id)) {
      result.models.removed.push(id);
      result.models.hasDifferences = true;
    }
  }

  // 変更されたモデル
  for (const id of originalIds) {
    if (!modifiedSet.has(id)) continue;

    if (hasChanges(original.models[id], modified.models[id])) {
      result.models.modified.push(id);
      result.models.hasDifferences = true;
    }
  }
}

/**
 * レポートの差分を計算
 */
function diffReports(
  original: MIDASProject,
  modified: MIDASProject,
  result: ProjectDiff
): void {
  const originalReports = original.reports || [];
  const modifiedReports = modified.reports || [];

  // レポートはid属性を持つので、idベースで比較
  const originalById = new Map(originalReports.map((r) => [r.id as ReportId, r]));
  const modifiedById = new Map(modifiedReports.map((r) => [r.id as ReportId, r]));

  // 追加されたレポート
  for (const [id] of modifiedById) {
    if (!originalById.has(id)) {
      result.reports.added.push(id);
      result.reports.hasDifferences = true;
    }
  }

  // 削除されたレポート
  for (const [id] of originalById) {
    if (!modifiedById.has(id)) {
      result.reports.removed.push(id);
      result.reports.hasDifferences = true;
    }
  }

  // 変更されたレポート
  for (const [id, originalReport] of originalById) {
    const modifiedReport = modifiedById.get(id);
    if (!modifiedReport) continue;

    const reportDiff = diffReport(originalReport, modifiedReport);
    if (reportDiff.hasDifferences) {
      result.reports.modified[id] = reportDiff;
      result.reports.hasDifferences = true;
    }
  }
}

/**
 * ワークスペースの差分を計算
 */
function diffWorkspaces(
  original: MIDASProject,
  modified: MIDASProject,
  result: ProjectDiff
): void {
  const originalWorkspaces = original.workspaces || [];
  const modifiedWorkspaces = modified.workspaces || [];

  // ワークスペースはid属性を持つので、idベースで比較
  const originalById = new Map(originalWorkspaces.map((w) => [w.id, w]));
  const modifiedById = new Map(modifiedWorkspaces.map((w) => [w.id, w]));

  // 追加されたワークスペース
  for (const [id] of modifiedById) {
    if (!originalById.has(id)) {
      result.workspaces.added.push(id);
      result.workspaces.hasDifferences = true;
    }
  }

  // 削除されたワークスペース
  for (const [id] of originalById) {
    if (!modifiedById.has(id)) {
      result.workspaces.removed.push(id);
      result.workspaces.hasDifferences = true;
    }
  }

  // 変更されたワークスペース
  for (const [id, originalWorkspace] of originalById) {
    const modifiedWorkspace = modifiedById.get(id);
    if (!modifiedWorkspace) continue;

    if (hasChanges(originalWorkspace, modifiedWorkspace)) {
      result.workspaces.modified.push(id);
      result.workspaces.hasDifferences = true;
    }
  }
}
